import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { Identity, PendingMessage } from "@/features/chat/types";

export const conversationKey = (identity: Identity, chatId: string) =>
  `${identity.clientId}:${identity.memberId}:${chatId}`;

interface ChatStore {
  embeddedChatCount: number;
  enterEmbeddedChat: () => void;
  leaveEmbeddedChat: () => void;
  handlerTip: boolean;
  setHandlerTip: (visible: boolean) => void;
  open: boolean;
  expanded: boolean;
  drafts: Record<string, string>;
  draftMentions: Record<string, string[]>;
  pending: Record<string, PendingMessage[]>;
  setOpen: (open: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  setDraft: (key: string, draft: string) => void;
  setDraftMentions: (key: string, mentions: string[]) => void;
  putPending: (key: string, message: PendingMessage) => void;
  removePending: (key: string, reqKey: string) => void;
  clearPrivate: () => void;
}

/** Only unsent work is persisted. Server messages belong exclusively to Query. */
export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      embeddedChatCount: 0,
      enterEmbeddedChat: () =>
        set((state) => ({
          embeddedChatCount: state.embeddedChatCount + 1,
          open: false,
        })),
      leaveEmbeddedChat: () =>
        set((state) => ({
          embeddedChatCount: Math.max(0, state.embeddedChatCount - 1),
        })),
      handlerTip: false,
      setHandlerTip: (handlerTip) => set({ handlerTip }),
      open: false,
      expanded: false,
      drafts: {},
      draftMentions: {},
      pending: {},
      setOpen: (open) => set({ open }),
      setExpanded: (expanded) => set({ expanded }),
      setDraft: (key, draft) =>
        set((state) => ({ drafts: { ...state.drafts, [key]: draft } })),
      setDraftMentions: (key, mentions) =>
        set((state) => ({
          draftMentions: { ...state.draftMentions, [key]: mentions },
        })),
      putPending: (key, message) =>
        set((state) => ({
          pending: {
            ...state.pending,
            [key]: [
              ...(state.pending[key] ?? []).filter(
                (item) => item.reqKey !== message.reqKey,
              ),
              message,
            ],
          },
        })),
      removePending: (key, reqKey) =>
        set((state) => ({
          pending: {
            ...state.pending,
            [key]: (state.pending[key] ?? []).filter(
              (item) => item.reqKey !== reqKey,
            ),
          },
        })),
      clearPrivate: () => set({ drafts: {}, draftMentions: {}, pending: {} }),
    }),
    {
      name: "secret-missions-chat-drafts",
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ drafts, draftMentions, pending }) => ({
        drafts,
        draftMentions,
        pending,
      }),
      // A page reload makes an in-flight request's outcome unknown. Retrying
      // with the original key lets the server reconcile it without duplication.
      merge: (persisted, current) => {
        const saved = persisted as
          Pick<ChatStore, "drafts" | "draftMentions" | "pending"> | undefined;
        return {
          ...current,
          drafts: saved?.drafts ?? {},
          draftMentions: saved?.draftMentions ?? {},
          pending: Object.fromEntries(
            Object.entries(saved?.pending ?? {}).map(([key, items]) => [
              key,
              items.map((item) => ({ ...item, state: "failed" as const })),
            ]),
          ),
        };
      },
    },
  ),
);
